import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { I18nProvider } from 'react-aria';
import { Provider } from 'react-aria-components';
import { timeZonesToRemove, validTimezones } from '@gonfalon/datetime/timezones';
import { CalendarDate, parseAbsoluteToLocal, Time } from '@internationalized/date';
import type { DatePickerProps as AriaDatePickerProps, TimeFieldProps } from '@launchpad-ui/components';
import {
  Button,
  ButtonContext,
  Calendar,
  CalendarCell,
  CalendarGrid,
  ComboBox,
  DateInput,
  DatePicker,
  DateSegment,
  Dialog,
  Group,
  Heading,
  IconButton,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  PopoverContext,
  TimeField,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { clsx } from 'clsx';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

import styles from './DateTimePicker.module.css';

interface TimezoneProps {
  tzid: string;
  onChange?: (tzid: string) => void;
  label?: ReactNode;
  enabled?: boolean;
  className?: string;
}

interface DatePickerProps extends AriaDatePickerProps<CalendarDate> {
  label?: ReactNode;
  className?: string;
  defaultDate?: number;
  minDate?: number;
  maxDate?: number;
}

export interface TimePickerProps extends TimeFieldProps<Time> {
  label?: ReactNode;
  className?: string;
}

export interface DateTimePickerProps {
  className?: string;
  /** Controlled value as epoch milliseconds. When provided, the displayed date/time are
   *  derived from this (in the selected timezone) and the component is controlled — the
   *  parent owns the value and must update it via `onChange`. Omit to use uncontrolled
   *  mode seeded from `datepickerProps.defaultDate`. */
  value?: number;
  onChange?: (value: number) => void;
  name?: string;
  /** Disables the whole control — date picker, time field, and timezone combobox. */
  isDisabled?: boolean;
  /** Disallow selecting dates/times in the past. Sugar over React Aria's standard `minValue`
   *  (https://react-aria.adobe.com/DatePicker#minvalue): resolves to `minValue = now()`, which
   *  floors the calendar to today and rejects past times. Combined with an explicit
   *  `datepickerProps.minDate`, the later of the two wins. */
  disablePast?: boolean;
  timepickerProps?: TimePickerProps;
  datepickerProps?: DatePickerProps;
  timezoneProps: TimezoneProps;
}

export function DateTimePicker({
  name,
  className,
  value,
  onChange,
  isDisabled,
  disablePast,
  timepickerProps = {},
  datepickerProps = {},
  timezoneProps,
}: DateTimePickerProps) {
  const datePickerButtonRef = useRef<HTMLButtonElement>(null);
  const {
    tzid: defaultTzid,
    label: timezoneLabel,
    enabled: timezoneEnabled = true,
    onChange: onChangeTimezone,
  } = timezoneProps;
  const {
    label: datepickerLabel = 'Date',
    defaultDate,
    minDate,
    maxDate,
    className: datepickerClassName,
    ...restDatepickerProps
  } = datepickerProps;
  const { label: timepickerLabel = 'Time', className: timepickerClassName, ...restTimepickerProps } = timepickerProps;
  const [filterTz, setFilterTz] = useState(defaultTzid);
  const [tzid, setTzid] = useState(defaultTzid);
  const tzidRef = useRef(defaultTzid);
  tzidRef.current = tzid;
  // Initialize date/time state from props: use toZonedTime when tzid is set so display is in that zone
  const [selectedDate, setSelectedDate] = useState<CalendarDate | undefined>(() => {
    if (defaultDate) {
      const date = defaultTzid ? toZonedTime(defaultDate, defaultTzid) : new Date(defaultDate);
      return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
    }
    return undefined;
  });

  const [selectedTime, setSelectedTime] = useState<Time | undefined>(() => {
    if (defaultDate) {
      const date = defaultTzid ? toZonedTime(defaultDate, defaultTzid) : new Date(defaultDate);
      return new Time(date.getHours(), date.getMinutes(), 0, 0);
    }
    return undefined;
  });
  // `disablePast` floors the minimum to "now" (React Aria's standard `minValue`); if an explicit
  // minDate is also set, the later wins.
  const effectiveMinDate = disablePast ? Math.max(Date.now(), minDate ?? 0) : minDate;
  // TODO(DSYS-191): `minValue` is derived in the browser's local tz via parseAbsoluteToLocal, while
  // `validateTime` uses the selected `tzid`. These can disagree by a day around midnight when the
  // picked zone differs from the browser. Resolve when both bounds move to the DateValue contract.
  const minValue = effectiveMinDate ? parseAbsoluteToLocal(new Date(effectiveMinDate).toISOString()) : undefined;
  const maxValue = maxDate ? parseAbsoluteToLocal(new Date(maxDate).toISOString()) : undefined;

  // Controlled vs uncontrolled: when `value` (epoch ms) is provided, derive the displayed
  // date/time from it in the current timezone; otherwise fall back to internal state.
  const isControlled = value !== undefined;
  const controlledZoned = isControlled ? (tzid ? toZonedTime(value, tzid) : new Date(value)) : undefined;
  const effectiveDate = isControlled
    ? controlledZoned &&
      new CalendarDate(controlledZoned.getFullYear(), controlledZoned.getMonth() + 1, controlledZoned.getDate())
    : selectedDate;
  const effectiveTime = isControlled
    ? controlledZoned && new Time(controlledZoned.getHours(), controlledZoned.getMinutes(), 0, 0)
    : selectedTime;

  const timezoneOptions = validTimezones
    .map((tz) => ({ value: tz, label: tz }))
    .filter((tz) => !timeZonesToRemove.includes(tz.value));

  // Validate that the selected time is not in the past
  const validateTime = (time: Time | undefined) => {
    if (!effectiveDate || !time || !effectiveMinDate) {
      return null;
    }

    const fullDate = new Date(
      effectiveDate.year,
      effectiveDate.month - 1,
      effectiveDate.day,
      time.hour,
      time.minute,
      0,
      0,
    );

    let utcMs: number;

    if (tzid) {
      utcMs = fromZonedTime(fullDate, tzid).getTime();
    } else {
      utcMs = fullDate.getTime();
    }

    if (utcMs < effectiveMinDate) {
      return 'Selected time must be in the future';
    }

    return null;
  };

  const onChangeCallback = (params: {
    time?: Time | undefined;
    date?: CalendarDate | undefined;
    timeZone?: string | undefined;
  }) => {
    const { date = effectiveDate, timeZone = tzid } = params;
    const time = params.time ?? effectiveTime ?? new Time(0, 0, 0, 0);
    if (!date) {
      return null;
    }

    const localDate = new Date(
      date.year,
      date.month - 1, // JavaScript months are 0-based
      date.day,
      time.hour,
      time.minute,
      time.second,
      time.millisecond,
    );

    const newDate = timeZone ? fromZonedTime(localDate, timeZone).getTime() : localDate.getTime();

    if (timeZone) {
      onChangeTimezone?.(timeZone);
    }
    onChange?.(newDate);
  };

  return (
    <div className={clsx(styles.dateTimePicker, className)}>
      {/* Force en-US locale so date/time segments always render MM/DD/YYYY and 12-hour time,
         matching the legacy DayInput/TimePicker behavior. Without this, browsers in 24-hour
         locales show DD.MM.YYYY + HH:MM which causes date-entry bugs in some browsers. */}
      <I18nProvider locale="en-US">
        <DatePicker
          // Spread first so the component-managed props below (value, isDisabled, …) win over
          // anything passed via datepickerProps. isDisabled is OR-merged so a per-field
          // datepickerProps.isDisabled still disables, while a top-level isDisabled always wins.
          {...restDatepickerProps}
          value={effectiveDate}
          minValue={minValue}
          maxValue={maxValue}
          onChange={(nextDate) => {
            setSelectedDate(nextDate ?? undefined);
            onChangeCallback({ date: nextDate ?? undefined });
          }}
          aria-label="Select date"
          className={clsx(styles.datePicker, datepickerClassName)}
          name={`${name}-date`}
          granularity="day"
          hideTimeZone
          isDisabled={isDisabled || restDatepickerProps.isDisabled}
        >
          {datepickerLabel && <Label>{datepickerLabel}</Label>}
          {/* Launchpad's DatePicker skips providing ButtonContext/PopoverContext when inside a Form, so the Popover has no triggerRef and renders at (0,0). Re-provide them here. */}
          <Provider
            values={[
              [
                ButtonContext,
                {
                  ref: datePickerButtonRef,
                  className: clsx(styles.datePickerButton, restDatepickerProps.isInvalid && styles.invalid),
                },
              ],
              [PopoverContext, { trigger: 'DatePicker', triggerRef: datePickerButtonRef, placement: 'bottom start' }],
            ]}
          >
            <Button>
              <Icon name="calendar" size="small" />
              <DateInput>{(segment) => <DateSegment segment={segment} />}</DateInput>
              <Icon name="chevron-down" size="small" />
            </Button>
            <Popover>
              <Dialog>
                <Calendar>
                  <header>
                    <IconButton
                      slot="previous"
                      icon="chevron-left"
                      aria-label="previous"
                      size="small"
                      variant="minimal"
                    />
                    <Heading />
                    <IconButton slot="next" icon="chevron-right" aria-label="next" size="small" variant="minimal" />
                  </header>
                  <CalendarGrid>{(date) => <CalendarCell date={date} />}</CalendarGrid>
                </Calendar>
              </Dialog>
            </Popover>
          </Provider>
        </DatePicker>
        <TimeField
          // Spread first so the component-managed props below win over anything passed via
          // timepickerProps; isDisabled is OR-merged (per-field disables, top-level always wins).
          {...restTimepickerProps}
          aria-label="Select time"
          className={clsx(styles.timeField, timepickerClassName)}
          name={`${name}-time`}
          validate={validateTime}
          value={effectiveTime}
          onChange={(nextTime) => {
            setSelectedTime(nextTime ?? undefined);

            onChangeCallback({ time: nextTime ?? undefined });
          }}
          isDisabled={isDisabled || restTimepickerProps.isDisabled}
        >
          {timepickerLabel && <Label>{timepickerLabel}</Label>}
          <Group>
            <Icon name="clock" size="small" />
            <DateInput>{(segment) => <DateSegment segment={segment} />}</DateInput>
          </Group>
        </TimeField>
      </I18nProvider>
      {timezoneEnabled && (
        <ComboBox
          aria-label="Select timezone"
          isDisabled={isDisabled}
          className={clsx(styles.timeZonePicker, timezoneProps.className)}
          inputValue={filterTz}
          defaultInputValue={tzid}
          onInputChange={(input) => setFilterTz(input)}
          onOpenChange={(open) => {
            if (open) {
              setFilterTz('');
            } else {
              setFilterTz(tzidRef.current);
            }
          }}
          onSelectionChange={(key) => {
            if (key === null || key === undefined) {
              return;
            }
            const next = key as string;
            setTzid(next);
            tzidRef.current = next;
            setFilterTz(next);
            if (isControlled) {
              // Controlled: `value` is an instant — preserve it across a timezone change.
              // The display re-derives from `value` in the new zone on re-render; rebuilding
              // the epoch from the old zone's wall-clock segments would silently shift it.
              onChangeTimezone?.(next);
              return;
            }
            onChangeCallback({ timeZone: next });
          }}
        >
          {timezoneLabel && <Label>{timezoneLabel}</Label>}
          <Group>
            <Input name={`${name}-timezone`} placeholder="Select timezone" />
            <IconButton aria-label="Show suggestions" icon="chevron-down" size="small" variant="minimal" />
          </Group>
          <Popover placement="bottom end" className={styles.popover}>
            <ListBox items={timezoneOptions}>
              {timezoneOptions.map(
                (option) =>
                  option.value.toLocaleLowerCase().includes(filterTz.toLocaleLowerCase()) && (
                    <ListBoxItem textValue={option.label} key={option.value} id={option.value}>
                      {option.label}
                    </ListBoxItem>
                  ),
              )}
            </ListBox>
          </Popover>
        </ComboBox>
      )}
    </div>
  );
}
